import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

export interface BreadcrumbItem {
  label: string;
  href?: string;
  schemaPosition: number;
}

export interface CTAConfig {
  label: string;
  href: string;
  onClick?: () => void;
  scrollTo?: string;
  variant?: "primary" | "secondary" | "text";
}

export interface NavItem {
  label: string;
  href?: string;
  scrollTo?: string;
  external?: boolean;
  icon?: ReactNode;
}

export type AudienceMode = "individual" | "organization";

export interface LayoutConfig {
  headerCTAs?: CTAConfig[];
  headerNavItems?: NavItem[];
  headerNavOverride?: NavItem[];
  breadcrumbs?: BreadcrumbItem[];
  currentPage?: string;
  fixedHeader?: boolean;
  showMobileMenu?: boolean;
  showPrivacyReassurance?: boolean;
}

// ── Audience Mode ─────────────────────────────────────────────────────────────

const AUDIENCE_KEY = "wl-audience-mode";

function getStoredAudienceMode(): AudienceMode {
  try {
    const stored = localStorage.getItem(AUDIENCE_KEY);
    if (stored === "organization") return "organization";
  } catch {
    /* SSR / private browsing */
  }
  return "individual";
}

// eslint-disable-next-line react-refresh/only-export-components -- co-located context helper; dev-HMR only, no runtime impact
export function setStoredAudienceMode(mode: AudienceMode) {
  try {
    localStorage.setItem(AUDIENCE_KEY, mode);
  } catch {
    /* noop */
  }
}

const defaultConfig: LayoutConfig = {
  fixedHeader: true,
  showMobileMenu: true,
  showPrivacyReassurance: true,
};

interface LayoutContextValue {
  config: LayoutConfig;
  setConfig: (config: Partial<LayoutConfig>) => void;
  resetConfig: () => void;
  audienceMode: AudienceMode;
  setAudienceMode: (mode: AudienceMode) => void;
}

const LayoutContext = createContext<LayoutContextValue>({
  config: defaultConfig,
  setConfig: () => {},
  resetConfig: () => {},
  audienceMode: "individual",
  setAudienceMode: () => {},
});

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [config, setConfigState] = useState<LayoutConfig>(defaultConfig);
  const [audienceMode, setAudienceModeState] = useState<AudienceMode>(
    getStoredAudienceMode,
  );
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  // Reset config synchronously during render when route changes.
  // This avoids the flash where DEFAULT_NAV shows before the page's
  // useLayoutEffect sets its override, because useEffect-based resets
  // fire after child effects (overwriting the page's config).
  // eslint-disable-next-line react-hooks/refs -- deliberate render-time ref read for the "adjust state on route change" pattern; moving to an effect reintroduces the DEFAULT_NAV flash described above
  if (location.pathname !== prevPathRef.current) {
    // eslint-disable-next-line react-hooks/refs -- see above; render-time write is required so children never see stale config
    prevPathRef.current = location.pathname;
    // Synchronous setState during render: React will restart the render
    // with the new state before committing, so children never see stale config
    setConfigState(defaultConfig);
  }

  const setConfig = useCallback((partial: Partial<LayoutConfig>) => {
    setConfigState((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetConfig = useCallback(() => {
    setConfigState(defaultConfig);
  }, []);

  const setAudienceMode = useCallback((mode: AudienceMode) => {
    setAudienceModeState(mode);
    setStoredAudienceMode(mode);
  }, []);

  return (
    <LayoutContext.Provider
      value={{ config, setConfig, resetConfig, audienceMode, setAudienceMode }}
    >
      {children}
    </LayoutContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- context hook imported by 45+ modules via the layouts barrel; moving is high-churn, dev-HMR only
export function useLayoutConfig() {
  return useContext(LayoutContext);
}
